import { appPrompt } from '../../services/appDialogs';
import React, { useState, useCallback, useEffect } from "react";
import {
  ReactFlow, Controls, Background, MiniMap,
  useNodesState, useEdgesState, addEdge,
  MarkerType,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Loader2, Plus, Trash2, BrainCircuit, Save } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { runTaskToCompletion } from "../../services/taskService";

// 图表序列色：React Flow 节点/连线/minimap 用色（SVG 属性对 var() 支持不稳），保留
const ROLE_COLORS = {
  protagonist: "#3B82F6",
  antagonist: "#EF4444",
  supporting: "#22C55E",
  minor: "#6B7280",
};

// 角色节点组件
function CharacterNode({ data, selected }) {
  const color = data.color || ROLE_COLORS[data.role] || "#6B7280";
  return (
    <div style={{
      padding: "10px 16px",
      borderRadius: 12,
      background: "var(--color-bg-secondary)",
      border: selected ? `2px solid ${color}` : `1.5px solid ${color}40`,
      boxShadow: selected ? `0 0 0 3px ${color}20, 0 4px 12px rgba(0,0,0,0.15)` : "0 2px 8px rgba(0,0,0,0.1)",
      minWidth: 100,
      textAlign: "center",
      transition: "all 0.2s",
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: "var(--color-text-primary)", marginBottom: 2 }}>
        {data.name}
      </div>
      {data.description && (
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", lineHeight: 1.3 }}>
          {data.description}
        </div>
      )}
      <div style={{ fontSize: 9, marginTop: 4, color, fontWeight: 600, textTransform: "uppercase", letterSpacing: 1 }}>
        {data.role === "protagonist" ? "主角" : data.role === "antagonist" ? "反派" : data.role === "supporting" ? "配角" : "路人"}
      </div>
    </div>
  );
}

const nodeTypes = { character: CharacterNode };

export default function CharacterRelationGraph({ projectId }) {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);

  // 加载已保存的图谱
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      setLoading(true);
      try {
        const json = await authenticatedFetch(`${API_HOST}/api/character-relation?projectId=${projectId}`);
        if (json) {
          const data = typeof json === "string" ? JSON.parse(json) : json;
          applyGraphData(data);
        }
      } catch (e) {
        console.warn("加载图谱失败:", e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const applyGraphData = (data) => {
    if (!data?.nodes) return;
    const flowNodes = data.nodes.map((n, i) => ({
      id: n.id,
      type: "character",
      position: n.position || { x: 150 + (i % 4) * 200, y: 100 + Math.floor(i / 4) * 160 },
      data: { name: n.name, description: n.description, role: n.role, color: n.color },
    }));
    const flowEdges = (data.edges || []).map((e, i) => ({
      id: `edge_${i}`,
      source: e.source,
      target: e.target,
      label: e.relation,
      style: { stroke: e.color || "#888", strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 600, fill: "var(--color-text-secondary)" },
      labelBgStyle: { fill: "var(--color-bg-secondary)", fillOpacity: 0.9 },
      labelBgPadding: [4, 6],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: e.color || "#888" },
      data: { relation: e.relation, description: e.description },
    }));
    setNodes(flowNodes);
    setEdges(flowEdges);
  };

  // AI 分析
  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      const provider = localStorage.getItem("ai_provider") || "omini";
      const { result } = await runTaskToCompletion(
        "CHARACTER_RELATION_ANALYZE",
        { projectId: Number(projectId), provider },
        {
          projectId: Number(projectId),
          provider,
          source: "character_relation",
          timeoutMs: 15 * 60 * 1000,
        }
      );
      const data = typeof result === "string" ? JSON.parse(result) : result;
      applyGraphData(data);
      toast.success(`分析完成，${data.nodes?.length || 0} 个角色，${data.edges?.length || 0} 条关系`);
    } catch (e) {
      toast.error("分析失败: " + e.message);
    } finally {
      setAnalyzing(false);
    }
  };

  // 保存
  const handleSave = async () => {
    setSaving(true);
    try {
      const data = {
        nodes: nodes.map(n => ({ id: n.id, name: n.data.name, description: n.data.description, role: n.data.role, color: n.data.color, position: n.position })),
        edges: edges.map(e => ({ source: e.source, target: e.target, relation: e.data?.relation || e.label, description: e.data?.description || "", color: e.style?.stroke })),
      };
      await authenticatedFetch(`${API_HOST}/api/character-relation`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectId: Number(projectId), relationJson: JSON.stringify(data) }),
      });
      toast.success("图谱已保存");
    } catch (e) {
      toast.error("保存失败: " + e.message);
    } finally {
      setSaving(false);
    }
  };

  // 手动添加角色
  const handleAddNode = async () => {
    const id = `char_${Date.now()}`;
    const name = (await appPrompt("角色名称"));
    if (!name) return;
    setNodes(nds => [...nds, {
      id,
      type: "character",
      position: { x: 200 + Math.random() * 300, y: 200 + Math.random() * 200 },
      data: { name, description: "", role: "supporting", color: ROLE_COLORS.supporting },
    }]);
  };

  // 连线
  const onConnect = useCallback(async (params) => {
    const relation = (await appPrompt("关系类型（如：恋人、仇敌、师徒）"));
    if (!relation) return;
    setEdges(eds => addEdge({
      ...params,
      label: relation,
      style: { stroke: "#888", strokeWidth: 2 },
      labelStyle: { fontSize: 10, fontWeight: 600, fill: "var(--color-text-secondary)" },
      labelBgStyle: { fill: "var(--color-bg-secondary)", fillOpacity: 0.9 },
      labelBgPadding: [4, 6],
      labelBgBorderRadius: 4,
      markerEnd: { type: MarkerType.ArrowClosed, width: 12, height: 12, color: "#888" },
      data: { relation, description: "" },
    }, eds));
  }, [setEdges]);

  // 删除选中
  const handleDeleteSelected = () => {
    const selectedNodeIds = nodes.filter(n => n.selected).map(n => n.id);
    const selectedEdgeIds = edges.filter(e => e.selected).map(e => e.id);
    if (selectedNodeIds.length === 0 && selectedEdgeIds.length === 0) return;
    setNodes(nds => nds.filter(n => !selectedNodeIds.includes(n.id)));
    setEdges(eds => eds.filter(e => !selectedEdgeIds.includes(e.id) && !selectedNodeIds.includes(e.source) && !selectedNodeIds.includes(e.target)));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* 工具栏 */}
      <div className="flex items-center gap-2 px-4 py-2.5" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
        <button onClick={handleAnalyze} disabled={analyzing}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
          {analyzing ? <Loader2 size={13} className="animate-spin" /> : <BrainCircuit size={13} />}
          {analyzing ? "分析中..." : "AI 生成图谱"}
        </button>
        <button onClick={handleAddNode}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all hover:bg-[var(--color-bg-tertiary)]"
          style={{ color: "var(--color-text-secondary)" }}>
          <Plus size={13} /> 添加角色
        </button>
        <button onClick={handleDeleteSelected}
          className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs transition-all hover:bg-[var(--color-bg-tertiary)]"
          style={{ color: "var(--color-text-muted)" }}>
          <Trash2 size={13} /> 删除选中
        </button>
        <div className="flex-1" />
        <button onClick={handleSave} disabled={saving}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all"
          style={{ backgroundColor: "var(--color-bg-tertiary)", color: "var(--color-text-secondary)" }}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          保存
        </button>
        <span className="text-2xs" style={{ color: "var(--color-text-muted)" }}>
          {nodes.length} 角色 · {edges.length} 关系
        </span>
      </div>

      {/* 画布 */}
      <div className="flex-1">
        {nodes.length === 0 && !analyzing ? (
          <div className="flex flex-col items-center justify-center h-full gap-3">
            <BrainCircuit size={40} style={{ color: "var(--color-text-muted)", opacity: 0.3 }} />
            <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>点击「AI 生成图谱」从大纲中提取角色关系</p>
            <p className="text-xs" style={{ color: "var(--color-text-muted)", opacity: 0.5 }}>或手动添加角色，拖线建立关系</p>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            nodeTypes={nodeTypes}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            deleteKeyCode="Delete"
          >
            <Controls position="bottom-right" />
            <Background gap={20} size={1} color="var(--color-border-secondary)" />
            <MiniMap
              nodeColor={(n) => n.data?.color || "#6B7280"}
              maskColor="rgba(0,0,0,0.1)"
              style={{ borderRadius: 8 }}
            />
          </ReactFlow>
        )}
      </div>
    </div>
  );
}
